import { z } from 'zod'
import { MAP_PRESET_IDS } from '@my-roadtrip/types'
import { latLngSchema, timestampSchema } from './common.schemas.js'

const stageTypeSchema = z.enum(['start', 'waypoint', 'end', 'overnight', 'poi'])

const coordinatesSchema = latLngSchema

const locationSchema = z.object({
  address: z.string()
    .min(1, "L'adresse est requise")
    .max(500, "L'adresse ne peut pas dépasser 500 caractères"),
  coordinates: coordinatesSchema,
  placeId: z.string().optional(),
  name: z.string()
    .min(1, 'Le nom doit contenir au moins 1 caractère')
    .max(200, 'Le nom ne peut pas dépasser 200 caractères')
    .optional()
})

const routeSettingsSchema = z.object({
  avoidHighways: z.boolean()
    .optional(),
  avoidTolls: z.boolean()
    .optional(),
  preferScenic: z.boolean()
    .optional(),
  maxDailyDistance: z.number()
    .int("La distance quotidienne doit être un nombre entier")
    .min(0, 'La distance quotidienne doit être positive')
    .optional(),
  restStops: z.boolean()
    .optional(),
  electricVehicle: z.boolean()
    .optional(),
  vehicleType: z.enum(['car', 'motorcycle', 'rv', 'bicycle', 'walking'])
    .optional(),
  fuelStops: z.boolean()
    .optional(),
  accommodationType: z.enum(['hotel', 'camping', 'mixed'])
    .optional(),
  budget: z.number()
    .min(0, 'Le budget doit être positif')
    .optional(),
  currency: z.string()
    .length(3, 'La devise doit comporter 3 caractères')
    .optional(),
  travelSpeed: z.enum(['relaxed', 'normal', 'fast'])
    .optional(),
  chargingStations: z.boolean()
    .optional()
})

const weatherSchema = z.object({
  temperature: z.number().optional(),
  condition: z.string().max(200).optional(),
  icon: z.string().optional()
}).partial()

// Stage practical info category schema
const stageInfoCategorySchema = z.object({
  key: z.enum(['parking', 'hours', 'price', 'food', 'access', 'booking', 'tips']),
  enabled: z.boolean(),
  value: z.string().max(500).optional(),
  details: z.object({
    type: z.string().max(100).optional(),
    cost: z.number().optional(),
    currency: z.string().max(3).optional(),
    opensAt: z.string().max(20).optional(),
    closesAt: z.string().max(20).optional(),
    bookingUrl: z.string().url().optional(),
    rating: z.number().min(0).max(5).optional(),
    notes: z.string().max(1000).optional()
  }).optional()
})

const stagePracticalInfoSchema = z.object({
  categories: z.array(stageInfoCategorySchema),
  tags: z.array(z.string().max(50)).optional(),
  updatedAt: timestampSchema.optional()
})

const accommodationSchema = z.object({
  name: z.string().max(200).optional(),
  address: z.string().max(500).optional(),
  price: z.number().min(0, 'Le prix doit être positif').optional(),
  checkIn: timestampSchema.optional(),
  checkOut: timestampSchema.optional()
}).partial()

const activitySchema = z.object({
  id: z.string().optional(),
  name: z.string().max(200).optional(),
  price: z.number().min(0, 'Le prix doit être positif').optional(),
  category: z.string().max(100).optional(),
  scheduledAt: timestampSchema.optional()
}).partial()

const photoSchema = z.object({
  url: z.string().url('URL invalide'),
  caption: z.string().max(500).optional()
}).partial()

const poiSchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  address: z.string().optional(),
  lat: z.number().optional(),
  lng: z.number().optional()
}).partial()

export const createPlanSchema = z.object({
  name: z.string()
    .min(1, 'Le nom du plan est requis')
    .max(200, 'Le nom ne peut pas dépasser 200 caractères'),
  description: z.string()
    .max(2000, 'La description ne peut pas dépasser 2000 caractères')
    .optional(),
  isPublic: z.boolean().default(false),
  isDefault: z.boolean().optional(),
  settings: routeSettingsSchema.optional()
})

export const updatePlanSchema = createPlanSchema.partial()

export const createStageSchema = z.object({
  name: z.string()
    .min(1, "Le nom de l'étape est requis")
    .max(200, "Le nom ne peut pas dépasser 200 caractères"),
  notes: z.string()
    .max(1000, "Les notes ne peuvent pas dépasser 1000 caractères")
    .optional(),
  location: locationSchema,
  type: stageTypeSchema,
  icon: z.string().max(100).optional(),
  order: z.number()
    .int("L'ordre doit être un nombre entier")
    .min(0, "L'ordre doit être positif")
    .max(999, "L'ordre ne peut pas dépasser 999"),
  duration: z.number().int().min(0).max(10080).optional(), // max 1 week in minutes
  day: z.number().int().min(0).optional(),
  estimatedArrival: timestampSchema.optional(),
  estimatedDeparture: timestampSchema.optional(),
  pois: z.array(poiSchema).optional(),
  accommodation: accommodationSchema.optional(),
  activities: z.array(activitySchema).optional(),
  practicalInfo: stagePracticalInfoSchema.optional(),
  photos: z.array(photoSchema).optional(),
  timeWindowStart: timestampSchema.optional().nullable(),
  timeWindowEnd: timestampSchema.optional().nullable(),
  checkInTime: timestampSchema.optional().nullable(),
  openingHours: z.object({
    opensAt: timestampSchema.optional().nullable(),
    closesAt: timestampSchema.optional().nullable(),
    timezone: z.string().max(50).optional().nullable()
  }).optional()
})

export const updateStageSchema = createStageSchema.partial().extend({
  // Nullable timestamps for update (can be cleared)
  estimatedArrival: timestampSchema.optional().nullable(),
  estimatedDeparture: timestampSchema.optional().nullable(),
  actualArrival: timestampSchema.optional().nullable(),
  actualDeparture: timestampSchema.optional().nullable(),
  completedAt: timestampSchema.optional().nullable(),
  // Weather data (from external API)
  weather: weatherSchema.optional()
})

export const optimizeRouteSchema = z.object({
  planId: z.string().uuid(),
  method: z.enum(['shortest', 'fastest']).default('shortest')
})

export const sharePlanSchema = z.object({
  userIds: z.array(
    z.string()
      .min(1, "L'identifiant utilisateur ne peut pas être vide")
  )
    .min(1, 'Au moins un utilisateur est requis')
    .max(25, 'Le partage est limité à 25 utilisateurs simultanés'),
  permissions: z.enum(['view', 'edit'])
})

export const updateSyncStateSchema = z.object({
  state: z.string()
    .min(1, "L'état de synchronisation est requis")
    .max(2_000_000, "L'état dépasse la taille maximale autorisée")
})

export const reorderStagesSchema = z.object({
  stageIds: z.array(
    z.string()
      .min(1, "L'identifiant d'étape est requis")
  )
    .min(1, 'Au moins une étape est requise')
})

export const generatePlanSchema = z.object({
  origin: z.string()
    .min(2, "Le point de départ est requis")
    .max(200, "Le point de départ ne peut pas dépasser 200 caractères"),
  destinations: z.array(
    z.string()
      .min(2, "Chaque destination doit comporter au moins 2 caractères")
      .max(200, "Une destination ne peut pas dépasser 200 caractères")
  ).min(1, 'Au moins une destination est requise'),
  days: z.number()
    .int('Le nombre de jours doit être un entier')
    .min(1, 'Le voyage doit durer au moins un jour')
    .max(60, 'Le voyage ne peut pas dépasser 60 jours'),
  travelers: z.enum(['solo', 'couple', 'family', 'friends']),
  travelMode: z.enum(['car', 'rv', 'motorcycle', 'bicycle']),
  pace: z.enum(['relaxed', 'balanced', 'fast']),
  themes: z.array(
    z.string()
      .min(2, 'Chaque thématique doit contenir au moins 2 caractères')
      .max(50, 'Chaque thématique est limitée à 50 caractères')
  ).min(1, 'Au moins une thématique est requise'),
  language: z.string()
    .regex(/^[a-z]{2}$/i, 'La langue doit être un code ISO à 2 lettres')
    .optional(),
  preferences: z.string()
    .max(1000, 'Les préférences ne peuvent pas dépasser 1000 caractères')
    .optional(),
  budgetLevel: z.enum(['lean', 'comfortable', 'premium'])
    .optional()
})

export const plannerPreferencesSchema = z.object({
  layoutPreset: z.enum(['balanced', 'focus-form', 'focus-map', 'compact', 'custom']).default('balanced'),
  panelOpen: z.boolean().default(true),
  defaultContext: z.enum(['pre', 'during', 'spark']).default('pre'),
  favoriteCategories: z.array(z.string().min(1).max(40)).max(12).default([]),
  defaultRadiusKm: z.number().min(5).max(120).default(25),
  lastUsedCategories: z.array(z.string().min(1).max(40)).max(12).optional(),
  mapPresetId: z.enum(MAP_PRESET_IDS).optional()
})
